import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Container,
  List,
  ListItem,
  ListItemText,
  Button,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from "@mui/material";
import { Image as ImageIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { createMembership, getMembershipListing } from "../apis/membershipApi";
import {
  attributeReferralCode,
  getMyReferralBenefit,
  validateReferralCode,
} from "../apis/referralApi";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useAuth } from "../context/AuthContext";
import { MembershipListing } from "../schemas/memberships";
import { toast } from "react-toastify";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GroupIcon from "@mui/icons-material/Group";
import SportsBaseballIcon from "@mui/icons-material/SportsBaseball";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import DiscountIcon from "@mui/icons-material/Discount";
import BadgeIcon from "@mui/icons-material/Badge";
import BlockIcon from "@mui/icons-material/Block";
import PeopleIcon from "@mui/icons-material/PeopleOutline";
import {
  applyFacilityMembershipPricing,
  DEFAULT_REGISTRATION_FEE_DOLLARS,
  formatEventDateRange,
  getActiveMembershipEvents,
  shouldWaiveRegistrationFee,
} from "../utils/facilityEvents";

const PENDING_REFERRAL_KEY = "pendingReferralCode";

interface PolicyFaq {
  question: string;
  answer: string;
}

interface Policy {
  title: string;
  points?: string[];
  faqs?: PolicyFaq[];
}

const PolicyBody: React.FC<{ policy: Policy; compact?: boolean }> = ({
  policy,
  compact = false,
}) => {
  if (policy.faqs?.length) {
    return (
      <Box>
        {policy.faqs.map((faq) => (
          <Box key={faq.question} sx={{ py: compact ? 1 : 1.5, "&:first-of-type": { pt: 0 } }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {faq.question}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {faq.answer}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <List dense disablePadding>
      {(policy.points || []).map((point, pointIndex) => (
        <ListItem key={point} sx={{ py: compact ? 0.25 : 0.5 }} disableGutters>
          <ListItemText
            primary={
              <Typography variant="body2">
                {`${String.fromCharCode(97 + pointIndex)}. ${point}`}
              </Typography>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

const Memberships: React.FC = () => {
  const membershipPlansQuery = useQuery({
    queryKey: ["membershipPlans"],
    queryFn: getMembershipListing,
  });
  const { data, isLoading } = membershipPlansQuery;
  const { userId } = useAuth();
  const [open, setOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [popupMessage] = useState("Please log in to confirm your booking.");
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referrerFirstName, setReferrerFirstName] = useState<string | null>(null);
  const [welcomeDiscountPercent, setWelcomeDiscountPercent] = useState(0);
  const [referralUsable, setReferralUsable] = useState(true);
  const [referralConflict, setReferralConflict] = useState<string | null>(null);
  const attributedRef = useRef<string | null>(null);
  const [codeHydrated, setCodeHydrated] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refFromUrl = params.get("ref");
    if (refFromUrl) {
      localStorage.setItem(PENDING_REFERRAL_KEY, refFromUrl);
      setReferralCode(refFromUrl);
    } else {
      const stored = localStorage.getItem(PENDING_REFERRAL_KEY);
      if (stored) setReferralCode(stored);
    }
    setCodeHydrated(true);
  }, []);

  useEffect(() => {
    if (!referralCode) return;
    let cancelled = false;
    validateReferralCode(referralCode)
      .then((result) => {
        if (cancelled || !result.valid) return;
        setReferrerFirstName(result.referrerFirstName);
        setWelcomeDiscountPercent(result.welcomeDiscountPercent || 0);

        const usable = result.canApply !== false;
        setReferralUsable(usable);
        setReferralConflict(
          usable
            ? null
            : result.conflictMessage ||
                "This referral link cannot be applied to your account"
        );
        // Stop a code they are not allowed to use from following them around.
        if (!usable) localStorage.removeItem(PENDING_REFERRAL_KEY);

        // Record the code against the account as soon as both are known, so the
        // referral outlives this browser instead of expiring with the stored
        // code. Attribution is idempotent, and a code that does not apply was
        // already reported above.
        const attributionKey = `${userId}:${referralCode}`;
        if (usable && userId && attributedRef.current !== attributionKey) {
          attributedRef.current = attributionKey;
          attributeReferralCode(referralCode).catch(() => {
            attributedRef.current = null;
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReferrerFirstName(null);
          setWelcomeDiscountPercent(0);
          setReferralUsable(true);
          setReferralConflict(null);
          localStorage.removeItem(PENDING_REFERRAL_KEY);
          setReferralCode(null);
        }
      });
    return () => {
      cancelled = true;
    };
    // userId matters: whether a code can be applied depends on who is signed in.
  }, [referralCode, userId]);

  // After a clean login the code is gone from the URL and localStorage, but
  // attribution on the user row still entitles them to the welcome discount.
  useEffect(() => {
    if (!userId || !codeHydrated || referralCode) return;
    let cancelled = false;
    getMyReferralBenefit()
      .then((benefit) => {
        if (cancelled || benefit.welcomeDiscountPercent <= 0) return;
        setReferrerFirstName(benefit.referrerFirstName);
        setWelcomeDiscountPercent(benefit.welcomeDiscountPercent);
        setReferralUsable(true);
        setReferralConflict(null);
      })
      .catch(() => {
        // Never referred is the common case; the page just shows no banner.
      });
    return () => {
      cancelled = true;
    };
  }, [userId, codeHydrated, referralCode]);

  // Policy data
  const policies: Policy[] = [
    {
      title: "Guest Policy:",
      points: [
        "Each membership allows up to 2 guests, with a fee of $19.99 per guest.",
        "Guests are permitted to play only while the membership holder is present in the cage.",
      ],
    },
    {
      title: "Lane/Cage Availability:",
      points: [
        "Members may use any available lane or cage.",
        "If a lane or cage is already booked, the member will be assigned to an available one.",
      ],
    },
    {
      title: "Free Session for Membership Holders:",
      points: [
        "Membership holders (with Quarterly, Semi-Annual, or Annual memberships) are entitled to one free session.",
        "Special exceptions may apply for birthdays or other significant occasions.",
      ],
    },
    {
      title: "Discount on Accessories:",
      points: [
        "Membership holders are entitled to a flat 15% discount on accessories.",
      ],
    },
    {
      title: "Referral FAQs:",
      faqs: [
        {
          question: "How do I refer someone?",
          answer:
            "Share your referral link or send an invite from your account dashboard. Only active members can refer.",
        },
        {
          question: "When do I get the discount?",
          answer:
            "After your friend completes paid membership signup. It is applied to your membership billing for as long as they stay active.",
        },
        {
          question: "Does the person I refer get anything?",
          answer:
            "Yes. They get 10% off their first membership payment, applied automatically at checkout. From their next billing cycle onwards they pay the regular price.",
        },
        {
          question: "What if my friend pauses or cancels?",
          answer:
            "A pause holds that 10% until they return. A cancellation removes that 10% permanently.",
        },
        {
          question: "What if I pause or cancel?",
          answer:
            "Pausing holds your referral discount. Cancelling forfeits every referral discount you have already earned.",
        },
        {
          question: "Does the discount apply to bookings?",
          answer: "No. It applies only to membership dues.",
        },
      ],
    },
    {
      title: "Identification Requirement:",
      points: [
        "Membership holders must present a valid piece of identification upon request.",
      ],
    },
    {
      title: "Equipment Policy:",
      points: [
        "Equipment may not be moved from one lane or cage to another without prior approval.",
      ],
    },
    {
      title: "Cancellation/Refund:",
      points: [
        "Transfer of Membership is not allowed.",
        "Memberships can only be cancelled in-person at the store.",
        "No refund allowed once a membership has been issued.",
        "Memberships cannot be cancelled on the same day it was purchased.",
      ],
    },
  ];

  const policyIcons = [
    <GroupIcon color="primary" />, // Guest Policy
    <SportsBaseballIcon color="primary" />, // Lane/Cage
    <CardGiftcardIcon color="primary" />, // Free Session
    <DiscountIcon color="primary" />, // Discount
    <PeopleIcon color="primary" />, // Referral FAQs
    <BadgeIcon color="primary" />, // ID
    <InfoIcon color="primary" />, // Equipment
    <BlockIcon color="error" />, // Cancellation
  ];

  const handlePurchase = (id: string) => {
    if(!userId) {
      setOpen(true)
      setOpenDialog(false);
      return;
    }
    setSelectedPlanId(id);
    setOpenDialog(true);
  };

  const handleConfirmPurchase = async () => {
    if (!selectedPlanId) return;
    if (!userId) {
      toast.error("You must be logged in to purchase a membership.");
      return;
    }

    try {
      const membershipData = {
        userId: userId,
        membershipListingId: selectedPlanId,
        ...(referralCode && referralUsable ? { referralCode } : {}),
      };
      const response = await createMembership(membershipData);
      console.log("Membership purchase response:", response);
      localStorage.removeItem(PENDING_REFERRAL_KEY);
      window.location.href = response.url;
    } catch (error: any) {
      console.error("Error handling purchase:", error);
      const errorMessage = error?.response?.data?.message;

      if (errorMessage === "User already has an active membership") {
        toast.error(
          "You already have an active membership. Please cancel it before purchasing a new one."
        );
      } else if (errorMessage) {
        toast.error(errorMessage);
      } else {
        toast.error("Unexpected error during membership purchase.");
      }
    } finally {
      setOpenDialog(false);
      setSelectedPlanId(null);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlanId(null);
  };

  // Swiper settings
  const swiperSettings = {
    modules: [Navigation, Pagination, Autoplay],
    spaceBetween: 20,
    slidesPerView: 3,
    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev",
    },
    pagination: {
      clickable: true,
      el: ".swiper-pagination",
    },
    autoplay: {
      delay: 5000,
      disableOnInteraction: true,
    },
    breakpoints: {
      0: {
        slidesPerView: 1,
        spaceBetween: 10,
      },
      600: {
        slidesPerView: 2,
        spaceBetween: 15,
      },
      960: {
        slidesPerView: 3,
        spaceBetween: 20,
      },
    },
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {referralConflict ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {referralConflict}. You can still purchase any membership below at the
          regular price.
        </Alert>
      ) : (
        referrerFirstName && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You were referred by {referrerFirstName}.
            {welcomeDiscountPercent > 0
              ? ` Get ${welcomeDiscountPercent}% off your first membership payment — renewals bill at the regular price.`
              : " Complete your membership signup to get started."}
          </Alert>
        )
      )}
      {/* Header Section */}
      <Box
        sx={{
          textAlign: "center",
          mb: 4,
          position: "relative",
          background: "linear-gradient(90deg, #e3f2fd 60%, #fce4ec 100%)",
          borderRadius: 4,
          py: 4,
          boxShadow: 2,
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: "bold",
            letterSpacing: 2,
            textTransform: "uppercase",
            background: "linear-gradient(90deg, #0a4d8c 50%, #d32f2f 50%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1,
          }}
        >
          Membership Plans
        </Typography>
        <Typography variant="h5" sx={{ color: "#0a4d8c", mb: 1 }}>
          Unlock exclusive benefits and savings!
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 600, mx: "auto", color: "#333" }}>
          Choose the perfect membership for your needs. Enjoy free sessions, guest access, discounts, and more. Join our community and elevate your game!
        </Typography>
        {getActiveMembershipEvents().map((event) => (
          <Alert
            key={event.id}
            severity="success"
            icon={<DiscountIcon />}
            sx={{ maxWidth: 640, mx: "auto", mt: 2, textAlign: "left" }}
          >
            <Typography fontWeight="bold">{event.name}</Typography>
            <Typography variant="body2">
              {(event.membershipPercentOff ?? 0) > 0
                ? `${event.membershipLabel ?? `${event.membershipPercentOff}% off membership`}. Valid ${formatEventDateRange(event)}. Checkout applies ${event.membershipPercentOff}% off the membership price.${event.waiveRegistrationFee ? " The registration fee is $0." : ""}`
                : event.waiveRegistrationFee
                  ? `${event.membershipLabel}. Valid ${formatEventDateRange(event)}. Your Stripe checkout will show the registration fee as $0.`
                  : `${event.membershipLabel}. Valid ${formatEventDateRange(event)}.`}
            </Typography>
          </Alert>
        ))}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 2,
            mb: 1,
            "& > span": {
              color: "#d32f2f",
              fontSize: "24px",
              mx: 0.5,
            },
          }}
        >
          <span>★</span>
          <span>★</span>
          <span>★</span>
        </Box>
      </Box>
      {/* Membership Cards Slider */}
      <Box sx={{ mb: 4, position: "relative" }}>
        {isLoading ? (
          <Typography variant="body1" textAlign="center">
            Loading membership plans...
          </Typography>
        ) : data && data.length > 0 ? (
          <>
            <Swiper {...swiperSettings}>
              {data.map((plan: MembershipListing) => (
                <SwiperSlide key={plan.id}>
                  <Card
                    sx={{
                      height: "100%",
                      border: "1px solid #ccc",
                      borderRadius: "16px",
                      overflow: "hidden",
                      boxShadow: "0 6px 16px rgba(10,77,140,0.10)",
                      transition: "transform 0.3s, box-shadow 0.3s",
                      position: "relative",
                    }}
                  >
                    {plan.imageUrl ? (
                      <CardMedia
                        component="img"
                        height="220"
                        image={plan.imageUrl}
                        alt={`${plan.name} membership`}
                        sx={{
                          objectFit: "cover",
                          borderTopLeftRadius: 16,
                          borderTopRightRadius: 16,
                        }}
                      />
                    ) : (
                      <Box
                        height="220px"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        bgcolor="grey.100"
                        sx={{
                          borderTopLeftRadius: 16,
                          borderTopRightRadius: 16,
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No Image Available
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ bgcolor: "#0a4d8c", color: "white", p: 2, textAlign: "center" }}>
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{ fontWeight: "bold", fontSize: { xs: "1.1rem", md: "1.25rem" } }}
                      >
                        {plan.name}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: "#fff", p: 2, textAlign: "center" }}>
                      {(() => {
                        const pricing = applyFacilityMembershipPricing(Number(plan.amount));
                        if (!pricing.promoActive || Number.isNaN(pricing.subtotal)) {
                          return (
                            <Typography variant="h4" component="div" sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                              ${plan.amount}
                            </Typography>
                          );
                        }
                        return (
                          <>
                            <Typography
                              variant="body1"
                              sx={{ textDecoration: "line-through", color: "text.secondary" }}
                            >
                              ${pricing.subtotal.toFixed(2)}
                            </Typography>
                            <Typography variant="h4" component="div" sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                              ${pricing.total.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="success.main">
                              {pricing.discountPercent}% off
                            </Typography>
                          </>
                        );
                      })()}
                      {shouldWaiveRegistrationFee() && (
                        <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                          Registration fee:{" "}
                          <Box
                            component="span"
                            sx={{ textDecoration: "line-through", color: "text.secondary", mr: 0.5 }}
                          >
                            ${DEFAULT_REGISTRATION_FEE_DOLLARS}
                          </Box>
                          <strong>$0</strong>
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ bgcolor: "#fff", textAlign: "center" }}>
                      <Button
                        onClick={() => handlePurchase(plan.id)}
                        variant="contained"
                        color="primary"
                        size="large"
                        sx={{
                          fontWeight: "bold",
                          borderRadius: "8px",
                          padding: "10px 24px",
                          textTransform: "none",
                          fontSize: "1.1rem",
                          boxShadow: 2,
                          transition: "background 0.2s",
                          mt: 1,
                        }}
                        endIcon={<InfoIcon />}
                      >
                        Purchase Now
                      </Button>
                    </Box>
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
            {/* Custom Navigation Arrows */}
            <Box
              className="swiper-button-prev"
              sx={{
                position: "absolute",
                top: "50%",
                left: "-40px",
                transform: "translateY(-50%)",
                color: "#0a4d8c",
                fontSize: "2rem",
                cursor: "pointer",
                zIndex: 10,
                "&:hover": {
                  color: "#d32f2f",
                },
                "&::after": {
                  fontSize: "2rem",
                },
              }}
            />
            <Box
              className="swiper-button-next"
              sx={{
                position: "absolute",
                top: "50%",
                right: "-40px",
                transform: "translateY(-50%)",
                color: "#0a4d8c",
                fontSize: "2rem",
                cursor: "pointer",
                zIndex: 10,
                "&:hover": {
                  color: "#d32f2f",
                },
                "&::after": {
                  fontSize: "2rem",
                },
              }}
            />
            <Box
              className="swiper-pagination"
              sx={{
                position: "relative",
                bottom: "-10px",
                "& .swiper-pagination-bullet": {
                  background: "#0a4d8c",
                  opacity: 0.6,
                  "&.swiper-pagination-bullet-active": {
                    background: "#d32f2f",
                    opacity: 1,
                  },
                },
              }}
            />
          </>
        ) : (
          <Typography variant="body1" textAlign="center">
            No membership plans available.
          </Typography>
        )}
      </Box>
      {/* Policies Section with Accordions */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2, color: "#0a4d8c" }}>
          Membership Policies & FAQs
        </Typography>
        {policies.map((policy, index) => (
          <Accordion key={index} sx={{ mb: 1, borderRadius: 2, boxShadow: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {policyIcons[index]}
                <Typography sx={{ ml: 1, fontWeight: "bold" }}>{policy.title}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <PolicyBody policy={policy} />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
      {/* Terms and Conditions Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleIcon color="primary" /> Terms and Conditions
        </DialogTitle>
        <DialogContent>
          {referrerFirstName && referralUsable && welcomeDiscountPercent > 0 && (
            <Alert severity="success" icon={<DiscountIcon />} sx={{ mb: 2 }}>
              Your {welcomeDiscountPercent}% referral discount will be applied to
              your first payment at checkout. Renewals bill at the regular price.
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please read these terms and conditions carefully before purchasing a membership:
          </Typography>
          <List dense>
            {policies.map((policy, index) => (
              <Box key={index}>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}
                >
                  {policyIcons[index]} {policy.title}
                </Typography>
                <PolicyBody policy={policy} compact />
              </Box>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary" variant="outlined" startIcon={<WarningAmberIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPurchase}
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
          >
            I Agree & Purchase
          </Button>
        </DialogActions>
      </Dialog>
      {/* Login Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="warning" /> Booking Alert
        </DialogTitle>
        <DialogContent>
          <Typography>{popupMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Memberships;